import { useState } from 'react';
import { Badge, Button, Dropdown, Empty, Tag, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { activitiesApi, type ActivityListItem } from '../../api/endpoints/activities';

const { Text } = Typography;

function itemLabel(a: ActivityListItem): string {
  const who = a.account?.name ?? a.accountNameSnapshot ?? '';
  const base = a.type ?? 'Actividad';
  return who ? `${base} · ${who}` : base;
}

/** Campana de recordatorios: actividades vencidas + de hoy del usuario. */
export function NotificationBell(): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['reminders'],
    queryFn: () => activitiesApi.reminders(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const total = (data?.overdueCount ?? 0) + (data?.todayCount ?? 0);

  const go = (id: number): void => {
    setOpen(false);
    navigate(`/actividades/${id}`);
  };

  const renderItem = (a: ActivityListItem, overdue: boolean): JSX.Element => (
    <div
      key={a.id}
      onClick={() => go(a.id)}
      style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Text style={{ fontSize: 13 }} ellipsis>
          {itemLabel(a)}
        </Text>
        {overdue ? (
          <Tag color="error" style={{ margin: 0 }}>
            {a.startDate ? dayjs(a.startDate).format('DD/MM') : 'vencida'}
          </Tag>
        ) : (
          <Tag color="processing" style={{ margin: 0 }}>
            {a.startDate ? dayjs(a.startDate).format('HH:mm') : 'hoy'}
          </Tag>
        )}
      </div>
      {a.notes && (
        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
          {a.notes}
        </Text>
      )}
    </div>
  );

  const panel = (
    <div
      style={{
        width: 340,
        maxHeight: 420,
        overflow: 'auto',
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
      }}
    >
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>Recordatorios</Text>
      </div>

      {total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin pendientes vencidos ni de hoy"
          style={{ padding: 16 }}
        />
      ) : (
        <>
          {(data?.overdue.length ?? 0) > 0 && (
            <>
              <div style={{ padding: '6px 12px', background: '#fff1f0' }}>
                <Text type="danger" style={{ fontSize: 12 }}>
                  Vencidas ({data?.overdueCount})
                </Text>
              </div>
              {data?.overdue.map((a) => renderItem(a, true))}
            </>
          )}
          {(data?.today.length ?? 0) > 0 && (
            <>
              <div style={{ padding: '6px 12px', background: '#e6f4ff' }}>
                <Text style={{ fontSize: 12, color: '#1677ff' }}>Hoy ({data?.todayCount})</Text>
              </div>
              {data?.today.map((a) => renderItem(a, false))}
            </>
          )}
        </>
      )}

      <div style={{ padding: 8, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setOpen(false);
            navigate('/calendario');
          }}
        >
          Ver calendario
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} dropdownRender={() => panel}>
      <Badge count={total} size="small" offset={[-2, 4]}>
        <Button type="text" aria-label="Recordatorios" icon={<BellOutlined style={{ fontSize: 18 }} />} />
      </Badge>
    </Dropdown>
  );
}
